import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Typography, Box, Paper } from '@mui/material';
import { ReportImage } from '@models/DomainModels/Master/report-image';
import { useCreateReportImageMutation, useUpdateReportImageMutation, useLazyFetchReportImageStreamQuery } from '@api/report-images/report-images-api';
import { useCustomSnackbar } from '@utils/snackbar-utils';
import { logger } from '@utils/logging-utils';
import { useFetchToken } from '@hooks/useFetchToken';
import { EntityDetailDialog, EntityDetailDialogWrapper } from '@commonComponents/dialogs/EntityDetailDialog';
import { DynamicStore } from '@state/interfaces/idynamic-store';
import { useDecoratorForm } from '@hooks/useDecoratorForm';
import { ReportImageApiModel } from '@models/ApiModels/report-image-api-model';
import InfoOutlined from '@mui-symbols-material/w400/InfoOutlined';
import { CdfFormField } from '@commonComponents/forms/fields/CdfFormField';
import styles from './ReportImageDialog.module.scss';

/**
 * Props for ReportImageDialog
 */
interface ReportImageDialogProps {
  /** Whether the dialog is open */
  readonly open: boolean;
  /** Callback when the dialog requests to be closed */
  readonly onClose: () => void;
  /** Callback when an operation (add/edit) is successfully completed */
  readonly onSuccess: () => void;
  /** Initial data for the form (for editing an existing image) */
  readonly initialData?: ReportImage;
  /** The section context ('header' | 'footer') */
  readonly section: 'header' | 'footer';
  /** Organization ID for API calls */
  readonly organizationId: string;
  /** Authentication token for API calls */
  readonly token: string;
  /** Read permission for the image */
  readonly readPermission?: boolean;
  /** Write permission for the image */
  readonly writePermission?: boolean;
  /** Dialog mode: 'add' or 'edit' */
  readonly dialogMode: 'add' | 'edit';
}

/**
 * Helper to convert File to Base64 string
 */
const convertFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
  });
};

/**
 * Presentational dialog for adding or editing a report image.
 * All logic is handled in the wrapper.
 */
function ReportImageDialog({
  open,
  onClose,
  onSuccess,
  initialData,
  section,
  organizationId,
  token,
  readPermission = false,
  writePermission = false,
  dialogMode
}: Readonly<ReportImageDialogProps>) {
  const { t } = useTranslation(['reportImages']);
  const { showSnackbar } = useCustomSnackbar();
  const [createReportImage] = useCreateReportImageMutation();
  const [updateReportImage] = useUpdateReportImageMutation();
  const [fetchReportImageStream] = useLazyFetchReportImageStreamQuery();

  // useDecoratorForm for form state and validation
  const {
    data: formData,
    errors,
    isValid: formIsValid,
    updateField,
    validateField,
    validateForm,
    resetForm,
    setData,
    setFormChanged,
    isDirty
  } = useDecoratorForm(
    ReportImageApiModel,
    initialData
      ? {
          name: initialData.name,
          pageSection: initialData.pageSection,
          filename: initialData.filename
        }
      : { name: '', pageSection: section, filename: '' },
    { showSnackbarOnError: true }
  );

  const [selectedFile, setSelectedFile] = React.useState<File | undefined>(undefined);
  const [isLoading, setIsLoading] = React.useState(false);
  const [fileType, setFileType] = React.useState<'header' | 'footer'>(section);
  const [imagePreviewUrl, setImagePreviewUrl] = React.useState<string | undefined>(
    initialData?.url ? `${initialData.url}?t=${new Date().getTime()}` : undefined
  );
  const [existingBinaryData, setExistingBinaryData] = React.useState<string | undefined>(undefined);

  // Comprehensive validation that includes all required fields
  const isFormCompletelyValid = React.useMemo(() => {
    // Basic form validation must pass
    if (!formIsValid) return false;

    // Name field must be filled and valid
    if (!formData.name?.trim()) return false;

    // In add mode, a file is required
    if (dialogMode === 'add' && !selectedFile) return false;

    // All validation checks passed
    return true;
  }, [formIsValid, formData.name, dialogMode, selectedFile]);

  // Stable reference to track when dialog opens for the first time or mode changes
  const [dialogKey, setDialogKey] = React.useState<string>('');
  const [isInitialized, setIsInitialized] = React.useState(false);

  // Generate a unique key for dialog state changes
  React.useEffect(() => {
    if (open) {
      const newKey = `${dialogMode}-${initialData?.id || 'new'}-${section}`;
      if (newKey !== dialogKey) {
        setDialogKey(newKey);
        setIsInitialized(false);
      }
    }
  }, [open, dialogMode, initialData?.id, section, dialogKey]);

  // Initialize form only once when dialog opens or key changes
  React.useEffect(() => {
    if (open && !isInitialized && dialogKey) {
      if (dialogMode === 'edit' && initialData) {
        setData(
          {
            name: initialData.name,
            pageSection: initialData.pageSection,
            filename: initialData.filename
          },
          true
        );
        setSelectedFile(undefined);
        setFileType(initialData.pageSection === 'Header' ? 'header' : 'footer');
        // Set the image preview URL directly from initialData.url
        setImagePreviewUrl(initialData.url);
      } else {
        setData({ name: '', pageSection: section, filename: '' }, true);
        setSelectedFile(undefined);
        setFileType(section);
        setImagePreviewUrl(undefined);
        setExistingBinaryData(undefined);
      }
      setIsInitialized(true);
    }
  }, [open, isInitialized, dialogKey, dialogMode, initialData, section, setData]);

  // Separate effect for fetching binary data in edit mode
  React.useEffect(() => {
    if (open && dialogMode === 'edit' && initialData?.id && isInitialized) {
      fetchReportImageStream({
        organizationId,
        reportImageId: initialData.id,
        token
      })
        .unwrap()
        .then(base64Data => {
          setExistingBinaryData(base64Data);
          logger.info('ReportImageDialog', 'Existing binary data loaded', {
            reportImageId: initialData.id,
            dataLength: base64Data.length
          });
        })
        .catch(error => {
          logger.warn('ReportImageDialog', 'Failed to load existing binary data', {
            reportImageId: initialData.id,
            error
          });
          // Don't show error to user as this is not critical
        });
    }
  }, [open, dialogMode, initialData?.id, isInitialized, fetchReportImageStream, organizationId, token]);

  const handleFieldChange = (field: string, value: string | number) => {
    updateField(field, value);
    setTimeout(() => validateField(field, value), 0);
  };

  // Handle field blur (validation)
  const handleFieldBlur = (field: string) => {
    validateField(field, formData[field as keyof ReportImageApiModel]);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setSelectedFile(undefined);
      updateField('filename', initialData?.filename ?? '');
      return;
    }

    // Validate file size
    if (file.size > 2 * 1024 * 1024) {
      showSnackbar(t('reportImages.errorFileSize'), 'error');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      showSnackbar(t('reportImages.errorFileType'), 'error');
      return;
    }

    // Validate image dimensions
    const reader = new FileReader();
    reader.onload = e => {
      const img = new window.Image();
      img.onload = () => {
        const { width, height } = img;
        if (width !== 2480) {
          showSnackbar(t('reportImages.errorImageWidth'), 'error');
          return;
        }
        // Different height limits for header vs footer images
        const maxHeight = fileType === 'header' ? 590 : 354;
        if (height < 200 || height > maxHeight) {
          const errorKey = fileType === 'header' ? 'errorImageHeightHeader' : 'errorImageHeightFooter';
          showSnackbar(t(`reportImages.${errorKey}`), 'error');
          return;
        }
        // All checks passed
        setSelectedFile(file);
        updateField('filename', file.name);
        setImagePreviewUrl(e.target?.result as string);
      };
      img.onerror = () => {
        showSnackbar(t('reportImages.errorReadingFile'), 'error');
      };
      img.src = e.target?.result as string;
    };
    reader.onerror = () => {
      showSnackbar(t('reportImages.errorReadingFile'), 'error');
    };
    reader.readAsDataURL(file);
  };

  const handleDialogClose = () => {
    resetForm();
    setFormChanged(false);
    setIsInitialized(false);
    setDialogKey('');
    onClose();
  };

  /**
   * Builds the API payload for create/update operations
   */
  const buildReportImagePayload = async (includeNewFile: boolean = false): Promise<ReportImageApiModel | null> => {
    const basePayload: ReportImageApiModel = {
      name: formData.name?.trim() ?? '',
      pageSection: fileType === 'header' ? 'Header' : 'Footer',
      filename: selectedFile ? selectedFile.name : (initialData?.filename ?? '')
    };

    // Add scope if provided (optional field)
    // Note: scope handling can be added here when needed

    // Add binaryData based on the scenario
    if (includeNewFile && selectedFile) {
      // New file selected - convert and include it
      try {
        const base64Data = await convertFileToBase64(selectedFile);
        basePayload.binaryData = base64Data;

        logger.info('ReportImageDialog', 'Including new file in payload', {
          fileName: selectedFile.name,
          fileSize: selectedFile.size,
          fileType: selectedFile.type,
          base64Length: base64Data.length
        });
      } catch (error) {
        logger.error('ReportImageDialog', 'Failed to convert file to base64', error);
        showSnackbar(t('reportImages.fileConversionError'), 'error');
        return null;
      }
    } else if (dialogMode === 'edit' && existingBinaryData) {
      // Edit mode with no new file - include existing binary data
      basePayload.binaryData = existingBinaryData;
      logger.info('ReportImageDialog', 'Including existing binary data in payload', {
        existingFilename: initialData?.filename,
        dataLength: existingBinaryData.length
      });
    }

    return basePayload;
  };

  /**
   * Validates the form and shows appropriate error messages
   * @returns True if valid, false otherwise
   */
  const validateAndCheckForm = async (): Promise<boolean> => {
    const valid = await validateForm();
    logger.info('ReportImageDialog', 'Form validation result', {
      isValid: valid.isValid,
      errors: valid.errors
    });

    if (!valid.isValid) {
      logger.warn('ReportImageDialog', 'Form validation failed', { errors: valid.errors });
      return false;
    }

    if (dialogMode === 'add' && !selectedFile) {
      logger.warn('ReportImageDialog', 'No file selected');
      showSnackbar(t('reportImages.fileRequiredError'), 'error');
      return false;
    }

    return true;
  };

  /**
   * Handles image update in edit mode
   */
  const handleUpdateImage = async () => {
    if (!initialData) return false;

    // Always try to include binary data in updates (either new file or existing data)
    const reportImageData = await buildReportImagePayload(true);
    if (!reportImageData) return false;

    logger.info('ReportImageDialog', 'Updating report image', {
      reportImageData,
      reportImageId: initialData.id,
      hasNewFile: !!selectedFile,
      hasExistingBinaryData: !!existingBinaryData,
      hasBinaryDataInPayload: !!reportImageData.binaryData
    });

    try {
      const result = await updateReportImage({
        organizationId,
        reportImageId: initialData.id,
        reportImageData,
        token
      }).unwrap();

      logger.info('ReportImageDialog', 'Update successful', { result });
      showSnackbar(t('reportImages.editSuccess'), 'success');
      return true;
    } catch (error) {
      logger.error('ReportImageDialog', 'Update failed', error);
      showSnackbar(t('reportImages.editError'), 'error');
      return false;
    }
  };

  /**
   * Handles image creation in add mode
   */
  const handleCreateImage = async () => {
    if (!selectedFile) return false;

    const reportImageData = await buildReportImagePayload(true);
    if (!reportImageData) return false;

    logger.info('ReportImageDialog', 'Creating report image', {
      reportImageData,
      hasNewFile: !!selectedFile
    });

    try {
      const result = await createReportImage({
        organizationId,
        reportImageData,
        token
      }).unwrap();

      logger.info('ReportImageDialog', 'Creation successful', { result });
      showSnackbar(t('reportImages.addSuccess'), 'success');
      return true;
    } catch (error) {
      logger.error('ReportImageDialog', 'Creation failed', error);
      showSnackbar(t('reportImages.addError'), 'error');
      return false;
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      logger.info('ReportImageDialog', 'Starting save process', {
        dialogMode,
        formData,
        hasSelectedFile: !!selectedFile,
        section: fileType
      });

      const isValid = await validateAndCheckForm();
      if (!isValid) {
        setIsLoading(false);
        return;
      }

      let success = false;

      if (dialogMode === 'edit' && initialData) {
        success = await handleUpdateImage();
      } else if (dialogMode === 'add') {
        success = await handleCreateImage();
      }

      if (success) {
        onSuccess();
        handleDialogClose();
      }
    } catch (error) {
      logger.error('ReportImageDialog', 'Error saving image', error);
      showSnackbar(t('reportImages.addError'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // --- UI Layout ---
  // Dialog title
  let titleText: string;
  if (dialogMode === 'edit') {
    titleText = writePermission ? t('reportImages.editHeaderImageTitle') : t('reportImages.viewHeaderImageTitle');
  } else {
    titleText = t('reportImages.addHeaderImageTitle');
  }

  // Info box content with section-specific size requirements
  const maxSizeKey = fileType === 'header' ? 'fileInfoMaxSizeHeader' : 'fileInfoMaxSizeFooter';
  const infoBox = (
    <Box className={styles.infoBox}>
      <Box className={styles.infoIcon}>
        <InfoOutlined fontSize="medium" aria-label="info" />
      </Box>
      <Box>
        <Typography variant="body2" component="div" className={styles.infoTitle}>
          {t('reportImages.fileInfoTitle')}
        </Typography>
        <ul className={styles.infoList}>
          <li>{t('reportImages.fileMaxSize')}</li>
          <li>{t('reportImages.fileInfoFormats')}</li>
          <li>{t('reportImages.fileInfoMinSize')}</li>
          <li>{t(`reportImages.${maxSizeKey}`)}</li>
          {fileType === 'header' && <li>{t('reportImages.SecondPageHeadersizeHint')}</li>}
        </ul>
      </Box>
    </Box>
  );

  // Image preview
  const imagePreview = imagePreviewUrl && (
    <Paper elevation={1} className={styles.imagePreviewPaper} sx={{ mb: 2 }}>
      <img src={imagePreviewUrl} alt={formData.filename ?? 'Preview'} className={styles.imagePreview} />
    </Paper>
  );

  return (
    <EntityDetailDialog
      open={open}
      onClose={handleDialogClose}
      title={titleText}
      formContent={
        <Box className={styles.formContainer}>
          {/* Upload button - always shown */}
          <Button
            variant="outlined"
            color="inherit"
            component="label"
            fullWidth
            disabled={isLoading ?? !writePermission}
            sx={{ mb: 2 }}
            data-testid="file-button">
            <Typography variant="body2">{t('reportImages.selectFileButton')}</Typography>
            <input className={styles.fileInput} type="file" onChange={handleFileChange} accept="image/png, image/jpeg, image/jpg" />
          </Button>

          {/* Image preview - shown when an image is available */}
          {imagePreview}

          {/* Editable file name field */}
          <CdfFormField
            name="name"
            label={t('reportImages.name')}
            value={formData.name ?? ''}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            errors={errors}
            fullWidth
            required={true}
            sx={{ mb: 2 }}
            slotProps={{
              input: {
                'aria-label': t('reportImages.name'),
                readOnly: !writePermission
              }
            }}
            data-testid="name-field"
          />

          {/* Info box - only shown when no image is selected */}
          {!imagePreviewUrl && infoBox}
        </Box>
      }
      saveButtonText={dialogMode === 'edit' ? t('common.save') : t('common.add')}
      cancelButtonText={writePermission ? t('common.cancel') : t('common.close')}
      readPermission={readPermission}
      writePermission={writePermission}
      isLoading={isLoading}
      isValid={isFormCompletelyValid}
      isDirty={isDirty()}
      onSave={handleSave}
      dataTestId="report-image-detail-dialog"
      maxWidth="sm"
    />
  );
}

interface ReportImageDialogWrapperProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialData?: ReportImage;
  section: 'header' | 'footer';
  organizationId: string;
  store?: DynamicStore;
  /** Dialog mode: 'add' | 'edit' */
  dialogMode: 'add' | 'edit';
}

export function ReportImageDialogWrapper({
  open,
  onClose,
  onSuccess,
  initialData,
  section,
  organizationId,
  store,
  dialogMode
}: Readonly<ReportImageDialogWrapperProps>) {
  const token = useFetchToken();
  return (
    <EntityDetailDialogWrapper
      organizationId={organizationId}
      store={store}
      entityType={'ReportImages'}
      renderContent={permissions => (
        <ReportImageDialog
          open={open}
          onClose={onClose}
          onSuccess={onSuccess}
          initialData={initialData}
          section={section}
          organizationId={organizationId}
          token={token}
          readPermission={permissions.readPermission}
          writePermission={permissions.writePermission}
          dialogMode={dialogMode}
        />
      )}
    />
  );
}
